@if (hasSignalRow) {
  <div class="d-flex mb-1" [ngClass]="signalRowClass">
    <div class="d-flex" [ngClass]="signalSlotStartClass">
      @if (!isClosed) {
        @if (!panel) {
          @if (urgency) {
            <span
              class="d-inline-flex align-items-center gap-1 font-11 fw-semibold flex-shrink-0"
              [ngClass]="'text-' + urgencyContext"
              [attr.aria-label]="urgencyLabel | translate"
              [matTooltip]="nextActionTooltip | translate"
            >
              <i [class]="urgencyIcon" class="font-12"></i>
              {{ urgencyLabel | translate }}
            </span>
          }

          @if (card?.nextAction?.dueAt) {
            <!--
              E4 RF-17 (plan §11, point 4): the next-action line opens the conclusion of that
              activity — a real button, reachable by keyboard. An untimed commitment prints its
              date without a time (RF-05).
            -->
            <button
              type="button"
              class="btn btn-link p-0 border-0 text-muted font-11 d-inline-flex align-items-center text-truncate text-decoration-none"
              [matTooltip]="'kanban.radar.concludeNextAction' | translate"
              [attr.aria-label]="('kanban.radar.concludeNextAction' | translate) + ' · ' + (nextActionTypeLabel | translate)"
              (click)="onConcludeNextAction($event)"
            >
              <i class="ri-flag-line me-1 flex-shrink-0"></i>
              <span class="text-truncate">
                @if (!compact) {
                  {{ nextActionTypeLabel | translate }} ·
                }
                {{ card.nextAction.dueAt | date: nextActionFormat }}
              </span>
            </button>
          } @else {
            <a
              href="javascript:void(0);"
              class="font-11 fw-semibold text-decoration-none d-inline-flex align-items-center text-truncate"
              (click)="onScheduleNextAction($event)"
              [attr.aria-label]="'kanban.radar.scheduleNextAction' | translate"
            >
              <i class="ri-add-line font-12 me-1 flex-shrink-0"></i>
              <span class="text-truncate">{{
                "kanban.radar.scheduleNextAction" | translate
              }}</span>
            </a>
          }
        }

        @if (card?.stagnant) {
          <span
            class="badge bg-soft-dark text-dark font-11 d-inline-flex align-items-center gap-1 flex-shrink-0"
            [matTooltip]="stagnantTooltip | translate"
            [attr.aria-label]="'kanban.radar.stagnant' | translate"
          >
            <i class="ri-hourglass-line font-12"></i>
            {{ "kanban.radar.stagnant" | translate }}
          </span>
        }
      }
    </div>

    <div class="d-flex" [ngClass]="signalSlotEndClass">
      @if (hasQuality) {
        <span
          class="badge font-11 d-inline-flex align-items-center gap-1 cursor-pointer"
          [ngClass]="
            'bg-soft-' + scoreBandContext + ' text-' + scoreBandContext
          "
          (click)="onOpenScoreExplanation($event)"
          [matTooltip]="'kanban.radar.scoreTooltip' | translate"
          [attr.aria-label]="
            'kanban.radar.scoreAria'
              | translate: { score: card.leadQuality.score }
          "
        >
          <i class="ri-focus-2-line font-12"></i>
          <span class="fw-bold">{{ card.leadQuality.score }}</span>
        </span>
      } @else if (!compact) {
        <span
          class="badge bg-soft-secondary text-muted font-11"
          [matTooltip]="'kanban.radar.qualityUnavailableTooltip' | translate"
        >
          <i class="ri-question-line me-1"></i>
          {{ "kanban.radar.qualityUnavailable" | translate }}
        </span>
      }
      @if (qualification) {
        <span
          class="badge font-11 d-inline-flex align-items-center gap-1"
          [ngClass]="
            isRecommendation
              ? 'border border-' +
                qualificationContext +
                ' text-' +
                qualificationContext
              : 'bg-soft-' +
                qualificationContext +
                ' text-' +
                qualificationContext
          "
          [matTooltip]="
            (isRecommendation
              ? 'kanban.radar.recommendationTooltip'
              : 'kanban.radar.decidedTooltip'
            ) | translate
          "
        >
          @if (isRecommendation) {
            <i class="ri-lightbulb-line font-12"></i>
          }
          {{ qualificationLabel | translate }}
        </span>
        @if (readyForPromotion) {
          <span
            class="badge bg-soft-success text-success font-11 d-inline-flex align-items-center gap-1"
            [matTooltip]="'kanban.radar.readyTooltip' | translate"
            [attr.aria-label]="'kanban.radar.readySeal' | translate"
          >
            <i class="ri-arrow-up-circle-line font-12"></i>
            {{ "kanban.radar.readySeal" | translate }}
          </span>
        }
      }

      @if (card?.leadQuality?.health) {
        <span
          class="badge font-11 d-inline-flex align-items-center gap-1"
          [ngClass]="'bg-soft-' + healthContext + ' text-' + healthContext"
          [matTooltip]="healthReasonTooltip"
          [attr.aria-label]="healthAria"
        >
          <i class="ri-heart-pulse-line font-12"></i>
          @if (hasHealthScore) {
            {{ card.leadQuality.health.score }} ·
          }
          {{ healthBandLabel | translate }}
        </span>
      }

      @if (card?.leadFlags?.outOfIcp) {
        <span
          class="badge bg-soft-warning text-warning font-11"
          [matTooltip]="'kanban.radar.outOfIcpTooltip' | translate"
        >
          <i class="ri-user-unfollow-line me-1"></i>
          {{ "kanban.radar.outOfIcp" | translate }}
        </span>
      }
    </div>
  </div>
}

@if (hasContextLine) {
  <div class="d-flex text-muted font-11" [ngClass]="contextRowClass">
    <span
      class="text-truncate d-inline-flex align-items-center"
      [ngClass]="contextTextClass"
    >
      @if (card?.leadOrigin?.type) {
        <i class="ri-map-pin-2-line me-1"></i>
        {{ originTypeLabel | translate }}
        @if (card?.leadOrigin?.sourceName) {
          · {{ card.leadOrigin.sourceName }}
        }
      } @else if (card?.lastInteraction?.at) {
        <i class="ri-chat-history-line me-1"></i>
        {{ card.lastInteraction.at | date: "d MMM" }} ·
        {{ lastInteractionTypeLabel | translate }}
        @if (card?.lastInteraction?.outcome) {
          · {{ lastInteractionOutcomeLabel | translate }}
        }
      }
    </span>

    @if (ageLabel) {
      <span
        class="d-inline-flex align-items-center flex-shrink-0"
        [matTooltip]="'kanban.radar.agesTooltip' | translate"
      >
        <i class="ri-time-line me-1"></i>
        {{ ageLabel }}
      </span>
    }
  </div>

  @if (card?.leadOrigin?.type && card?.lastInteraction?.at) {
    <div class="text-muted font-11 text-truncate">
      <i class="ri-chat-history-line me-1"></i>
      {{ card.lastInteraction.at | date: "d MMM" }} ·
      {{ lastInteractionTypeLabel | translate }}
      @if (card?.lastInteraction?.outcome) {
        · {{ lastInteractionOutcomeLabel | translate }}
      }
    </div>
  }
}

@if (showCadence) {
  <div
    class="card-radar-cadence-slot d-flex align-items-center gap-1 text-muted font-11 mt-1"
    [matTooltip]="cadenceAria"
    [attr.aria-label]="cadenceAria"
  >
    <i class="ri-phone-line"></i>
    <span class="font-monospace" aria-hidden="true">{{ cadenceDots }}</span>
    <span>{{ card.cadence.attempts }}/{{ cadenceMax }}</span>
    @if (card.cadence.exhausted) {
      <span class="badge bg-soft-danger text-danger font-11">
        {{ "kanban.radar.exhausted" | translate }}
      </span>
    }
  </div>
}
@if (lossLabel) {
  <div
    class="d-flex align-items-center text-danger font-11 mt-1"
    [matTooltip]="card.lossNote ?? ''"
  >
    <i class="ri-close-circle-line me-1 flex-shrink-0"></i>
    <span class="text-truncate">{{ lossLabel }}</span>
  </div>
}
